import type { Option } from "@tento-lona/sheets";
import { LiveData } from "@tento-core/livedata";
import { Weekday } from "@tento-chrono";
import { DependencyAspect } from "@tento-core/dependency-graph";
import { LedgerKeys } from "@tento-lona/sheets/internal-sdk";
import type { LedgerBumper } from "@tento-lona/sheets/internal-sdk";
import type { ClientLifecycle } from "../client/handle-errors.ts";
import type { PreferencesStorage } from "./preferences-storage.ts";
import { Actor, type InvalidationSink, CalendarPreferencesChanged } from "../invalidation.ts";
import { StoredPreference } from "./stored-preference.ts";

/** The day a week starts on. */
export type FirstDay = "sunday" | "monday";
/** How times of day are written: 12-hour or 24-hour. */
export type TimeFormat = "12h" | "24h";
/** How dates are written: month before day, or day before month. */
export type DateFormat = "month-day" | "day-month";
/**
 * A secondary calendar system a sheet's header can show beside the Gregorian
 * date.
 */
export type CalendarSystem = "lunar" | "hebrew" | "islamic" | "persian" | "thai" | "ethiopian" | "indian";

const FIRST_DAYS: readonly FirstDay[] = ["sunday", "monday"];
const TIME_FORMATS: readonly TimeFormat[] = ["12h", "24h"];
const DATE_FORMATS: readonly DateFormat[] = ["month-day", "day-month"];
const CALENDAR_SYSTEMS: readonly CalendarSystem[] = [
  "lunar",
  "hebrew",
  "islamic",
  "persian",
  "thai",
  "ethiopian",
  "indian",
];

const STORAGE_KEYS = {
  firstDay: "lona-pref-first-day",
  timeFormat: "lona-pref-time-format",
  dateFormat: "lona-pref-date-format",
  secondaryTimezones: "lona-pref-secondary-timezones",
  secondaryCalendars: "lona-pref-secondary-calendars",
  defaultWeatherLocation: "lona-pref-default-weather-location",
} as const;

/**
 * The calendar display preferences (first day of the week, time and date
 * formats, secondary time zones and calendar systems, weather location),
 * stored locally (`client.preferences.calendar()`). `peek()` reads them;
 * `set(key, value, actor)` is the one writer: it stores the value, updates
 * its live value and announces `CalendarPreferencesChanged` stamped with the
 * writer. `live` holds each value, read-only, to observe.
 */
export class CalendarPreferences {
  static readonly ACTOR = new Actor("calendar-preferences");
  /** Each preference as a read-only live value, to observe (`peek()` reads them). */
  readonly live: CalendarPreferences.Live;
  readonly #values: { readonly [K in CalendarPreferences.Key]: LiveData<CalendarPreferences.Values[K]> };
  /** The owning client's liveness: `set` checks it first. */
  readonly #lifecycle: ClientLifecycle;

  readonly #storage: PreferencesStorage;
  readonly #invalidation: InvalidationSink;
  /**
   * Row-identity ledger bumper (L2). Late-bound: the accessor that owns the
   * ledger is constructed after this preference client, so `PreferencesClient`
   * passes what it has and re-attaches via `attachLedger` once available.
   * `null` until then — bumps become no-ops.
   */
  #ledger: Option<LedgerBumper>;

  /** `lifecycle`: the owning client's (a standalone face: `new LonaSdk.Client.Lifecycle()`). */
  constructor(
    storage: PreferencesStorage,
    invalidation: InvalidationSink,
    ledger: Option<LedgerBumper>,
    lifecycle: ClientLifecycle,
  ) {
    this.#storage = storage;
    this.#invalidation = invalidation;
    this.#ledger = ledger;
    this.#lifecycle = lifecycle;
    this.#values = {
      firstDay: new LiveData<FirstDay>(
        StoredPreference.enumAt(storage, STORAGE_KEYS.firstDay, FIRST_DAYS) ?? "sunday",
      ),
      timeFormat: new LiveData<TimeFormat>(
        StoredPreference.enumAt(storage, STORAGE_KEYS.timeFormat, TIME_FORMATS) ?? "12h",
      ),
      dateFormat: new LiveData<DateFormat>(
        StoredPreference.enumAt(storage, STORAGE_KEYS.dateFormat, DATE_FORMATS) ?? "month-day",
      ),
      secondaryTimezones: new LiveData<readonly string[]>(this.#loadTimezones()),
      secondaryCalendars: new LiveData<readonly CalendarSystem[]>(this.#loadCalendars()),
      // Unset (nothing stored, or empty) is no location chosen: never a stand-in city.
      defaultWeatherLocation: new LiveData<Option<string>>(
        storage.getItem(STORAGE_KEYS.defaultWeatherLocation) || null,
      ),
    };
    this.live = this.#values;
  }

  /** Late-bind the row-identity ledger (attached once the accessor exists). */
  attachLedger(ledger: LedgerBumper): void {
    this.#ledger = ledger;
  }

  /** LR1: bump the `pref:<kind>` VALUES node (dark in L2). */
  #bumpPref(kind: string): void {
    this.#ledger?.bump(LedgerKeys.pref(kind), DependencyAspect.VALUES, "pref");
  }

  /** Every calendar preference, read synchronously (nothing is fetched). */
  peek(): CalendarPreferences.Values {
    return {
      firstDay: this.#values.firstDay.get(),
      timeFormat: this.#values.timeFormat.get(),
      dateFormat: this.#values.dateFormat.get(),
      secondaryTimezones: this.#values.secondaryTimezones.get(),
      secondaryCalendars: this.#values.secondaryCalendars.get(),
      defaultWeatherLocation: this.#values.defaultWeatherLocation.get(),
    };
  }

  /**
   * Store `value` as the preference `key`, update its live value and
   * announce `CalendarPreferencesChanged` (kind `key`) stamped with `actor`,
   * the writer (`Actor.EXTERNAL` for a caller with no echo of its own).
   */
  set<K extends CalendarPreferences.Key>(key: K, value: CalendarPreferences.Values[K], actor: Actor): void {
    this.#lifecycle.assertLive("preferences.calendar.set");
    if (value == null) {
      // An unset preference (`defaultWeatherLocation: null`) stores nothing, so it loads unset again.
      this.#storage.removeItem(STORAGE_KEYS[key]);
    } else {
      this.#storage.setItem(STORAGE_KEYS[key], typeof value === "string" ? value : JSON.stringify(value));
    }
    this.#values[key].update(value);
    this.#invalidation.emit(new CalendarPreferencesChanged(actor, key));
    this.#bumpPref(key);
  }

  /** The first day of the week as a `Weekday` (`peek().firstDay`). */
  startOfWeek(): Weekday {
    return this.#values.firstDay.get() === "monday" ? Weekday.MON : Weekday.SUN;
  }

  #loadTimezones(): string[] {
    const key = STORAGE_KEYS.secondaryTimezones;
    const list = StoredPreference.array(key, StoredPreference.json(this.#storage, key));
    const zones: string[] = [];
    for (const entry of list ?? []) {
      if (typeof entry === "string" && entry.length > 0) {
        zones.push(entry);
      } else {
        StoredPreference.unusable(key, "entry is not a timezone name", JSON.stringify(entry));
      }
    }
    return zones;
  }

  #loadCalendars(): CalendarSystem[] {
    const key = STORAGE_KEYS.secondaryCalendars;
    const list = StoredPreference.array(key, StoredPreference.json(this.#storage, key));
    const systems: CalendarSystem[] = [];
    for (const entry of list ?? []) {
      const system = StoredPreference.oneOf(key, entry, CALENDAR_SYSTEMS);
      if (system != null) systems.push(system);
    }
    return systems;
  }
}

/** Types that go with `CalendarPreferences`: `Values`, `Key`, `Live`. */
export namespace CalendarPreferences {
  /** Every calendar preference's value (`peek()`). */
  export type Values = {
    readonly firstDay: FirstDay;
    readonly timeFormat: TimeFormat;
    readonly dateFormat: DateFormat;
    readonly secondaryTimezones: readonly string[];
    readonly secondaryCalendars: readonly CalendarSystem[];
    /** The weather location id `alias.default-weather` shows; `null` when none is chosen (the row's cells are empty). */
    readonly defaultWeatherLocation: Option<string>;
  };
  /** A preference's name: `set(key, value, actor)`'s `key`, the `CalendarPreferencesChanged` kind. */
  export type Key = keyof Values;
  /** Each preference as a read-only live value (`live`). */
  export type Live = { readonly [K in Key]: LiveData.Readable<Values[K]> };
}
